<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'

// Visor de la imagen de una manzana en pantalla completa, con zoom y flechas
// de desplazamiento como botones (sin depender de gestos). Lo usan la salida
// para mayores y el informe de conductores.

const props = defineProps({
  src: { type: String, default: null },
  alt: { type: String, default: '' },
  etiqueta: { type: String, default: '' },
})

const emit = defineEmits(['cerrar'])

const ZOOM_MIN = 1
const ZOOM_MAX = 4
const ZOOM_PASO = 0.5
const PAN_PASO = 60

const zoom = ref(ZOOM_MIN)
const panX = ref(0)
const panY = ref(0)

const limitePan = computed(() => (zoom.value - 1) * 160)

const limitarPan = () => {
  const lim = limitePan.value
  panX.value = Math.max(-lim, Math.min(lim, panX.value))
  panY.value = Math.max(-lim, Math.min(lim, panY.value))
}

const acercar = () => {
  zoom.value = Math.min(ZOOM_MAX, zoom.value + ZOOM_PASO)
  limitarPan()
}

const alejar = () => {
  zoom.value = Math.max(ZOOM_MIN, zoom.value - ZOOM_PASO)
  limitarPan()
}

const mover = (dx, dy) => {
  const lim = limitePan.value
  panX.value = Math.max(-lim, Math.min(lim, panX.value + dx))
  panY.value = Math.max(-lim, Math.min(lim, panY.value + dy))
}

// Cada imagen nueva arranca sin zoom ni desplazamiento.
watch(() => props.src, (src) => {
  zoom.value = ZOOM_MIN
  panX.value = 0
  panY.value = 0
  document.body.style.overflow = src ? 'hidden' : ''
}, { immediate: true })

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="src"
      class="mayores-visor"
      role="dialog"
      aria-modal="true"
      :aria-label="etiqueta || alt"
    >
      <button type="button" class="mayores-visor__cerrar" @click="emit('cerrar')">
        <i class="pi pi-times" aria-hidden="true" /> Cerrar
      </button>

      <div class="mayores-visor__lienzo">
        <img
          :src="src"
          :alt="alt"
          class="mayores-visor__img"
          :style="{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})` }"
        >

        <template v-if="zoom > ZOOM_MIN">
          <button
            type="button"
            class="mayores-visor__flecha mayores-visor__flecha--arriba"
            aria-label="Mover el mapa hacia arriba"
            @click="mover(0, -PAN_PASO)"
          >
            <i class="pi pi-chevron-up" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="mayores-visor__flecha mayores-visor__flecha--abajo"
            aria-label="Mover el mapa hacia abajo"
            @click="mover(0, PAN_PASO)"
          >
            <i class="pi pi-chevron-down" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="mayores-visor__flecha mayores-visor__flecha--izquierda"
            aria-label="Mover el mapa hacia la izquierda"
            @click="mover(-PAN_PASO, 0)"
          >
            <i class="pi pi-chevron-left" aria-hidden="true" />
          </button>
          <button
            type="button"
            class="mayores-visor__flecha mayores-visor__flecha--derecha"
            aria-label="Mover el mapa hacia la derecha"
            @click="mover(PAN_PASO, 0)"
          >
            <i class="pi pi-chevron-right" aria-hidden="true" />
          </button>
        </template>
      </div>

      <div class="mayores-visor__barra">
        <button
          type="button"
          class="mayores-visor__zoom"
          :disabled="zoom <= ZOOM_MIN"
          aria-label="Alejar"
          @click="alejar"
        >
          <i class="pi pi-minus" aria-hidden="true" />
        </button>
        <span class="mayores-visor__zoom-texto">{{ Math.round(zoom * 100) }}%</span>
        <button
          type="button"
          class="mayores-visor__zoom"
          :disabled="zoom >= ZOOM_MAX"
          aria-label="Acercar"
          @click="acercar"
        >
          <i class="pi pi-plus" aria-hidden="true" />
        </button>
      </div>
    </div>
  </Teleport>
</template>
